import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Buildings, Lock, ArrowsClockwise, MapPin, UsersThree, Globe, EnvelopeSimple, Copy, CheckCircle, HandCoins, LinkedinLogo, HandHeart, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SponsorshipProposalModal } from "../components/SponsorshipProposalModal";
import MatchingGiftAskScript from "../components/MatchingGiftAskScript";
import WinProbabilityBadge from "../components/WinProbabilityBadge";

const ScoreBadge = ({ score, pending }) => {
  if (pending || score < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-mono px-3 py-1 rounded-sm border border-zinc-700 text-zinc-500 bg-zinc-900" data-testid="match-score-badge">
        <ArrowsClockwise className="w-3.5 h-3.5 animate-spin" /> Analyzing...
      </span>
    );
  }
  let color = "text-red-400 bg-red-500/10 border-red-500/30";
  if (score >= 75) color = "text-emerald-400 bg-emerald-500/10 border-emerald-500/30";
  else if (score >= 50) color = "text-amber-400 bg-amber-500/10 border-amber-500/30";

  return (
    <span className={`inline-flex items-center gap-1 text-lg font-mono font-bold px-3 py-1 rounded-sm border ${color}`} data-testid="match-score-badge">
      {score}%
    </span>
  );
};

const CorporateScout = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scouts, setScouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [onlyMatchers, setOnlyMatchers] = useState(false);
  const [proposalScout, setProposalScout] = useState(null);
  const pollRef = useRef(null);

  const isPremium = user?.subscription_tier === "pro" || user?.role === "admin";

  useEffect(() => {
    if (isPremium) fetchScouts();
    else setLoading(false);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line

  const fetchScouts = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const res = await axios.get(`${API}/corporate-scout`);
      setScouts(res.data.scouts || []);
      setMessage(res.data.message || "");
      const pending = res.data.pending_count || 0;
      setPendingCount(pending);

      // Auto-poll if there are pending analyses
      if (pending > 0 && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          try {
            const pollRes = await axios.get(`${API}/corporate-scout`);
            setScouts(pollRes.data.scouts || []);
            const newPending = pollRes.data.pending_count || 0;
            setPendingCount(newPending);
            if (newPending === 0) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              toast.success("All companies analyzed!");
            }
          } catch { /* silent */ }
        }, 15000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load corporate matches");
    } finally {
      setLoading(false);
    }
  };

  const refreshScouts = async () => {
    setRefreshing(true);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    try {
      const res = await axios.post(`${API}/corporate-scout/refresh`, {});
      setScouts(res.data.scouts || []);
      const pending = res.data.pending_count || 0;
      setPendingCount(pending);
      toast.success("Refresh started! Results will update automatically.");

      if (pending > 0 && !pollRef.current) {
        pollRef.current = setInterval(async () => {
          try {
            const pollRes = await axios.get(`${API}/corporate-scout`);
            setScouts(pollRes.data.scouts || []);
            const newPending = pollRes.data.pending_count || 0;
            setPendingCount(newPending);
            if (newPending === 0) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              toast.success("All companies analyzed!");
            }
          } catch { /* silent */ }
        }, 15000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  const copyPitch = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Pitch letter copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const openInGmail = (scout) => {
    const subject = encodeURIComponent(`Partnership Opportunity — ${user?.nonprofit_profile?.org_name || "Local Nonprofit"} x ${scout.company_name}`);
    const body = encodeURIComponent(scout.warm_intro);
    const to = scout.contact_email ? encodeURIComponent(scout.contact_email) : "";
    window.open(`mailto:${to}?subject=${subject}&body=${body}`, "_self");
  };

  // Builds a LinkedIn People search URL pre-filled for CSR / Community Relations contacts in SC.
  // We never store or POST anything — it's just a deep link the user opens in a new tab.
  const linkedinSearchUrl = (scout) => {
    const company = (scout.company_name || "").replace(/\([^)]*\)/g, "").trim();
    const keywords = `"${company}" (CSR OR "community relations" OR "corporate giving") "South Carolina"`;
    return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(keywords)}`;
  };

  if (!isPremium) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4" data-testid="scout-locked">
          <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center mb-6">
            <Lock weight="duotone" className="w-8 h-8 text-zinc-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Corporate Scout</h2>
          <p className="text-zinc-400 mb-6 max-w-md">
            Discover corporate giving opportunities from South Carolina's largest employers, matched to your nonprofit's mission. Verified companies have confirmed giving programs from their official websites.
          </p>
          <Button
            onClick={() => navigate("/pricing")}
            className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
            data-testid="upgrade-scout-btn"
          >
            Upgrade to Premium
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const analyzedCount = scouts.filter(s => !s.pending && s.match_score >= 0).length;
  const strongCount = scouts.filter(s => s.match_score >= 70).length;
  const matcherCount = scouts.filter(s => s.matches_employee_gifts).length;
  const visibleScouts = onlyMatchers ? scouts.filter(s => s.matches_employee_gifts) : scouts;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="corporate-scout-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Corporate Scout</h1>
            <p className="text-zinc-400 mt-1">Corporate giving opportunities matched to your nonprofit</p>
          </div>
          <Button
            onClick={refreshScouts}
            disabled={refreshing || loading}
            className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
            data-testid="refresh-scouts-btn"
          >
            {refreshing ? (
              <><ArrowsClockwise className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
            ) : (
              <><ArrowsClockwise className="w-4 h-4 mr-2" />Refresh Matches</>
            )}
          </Button>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="loading-spinner mb-4" style={{ width: 32, height: 32 }} />
            <p className="text-zinc-400">Loading corporate matches...</p>
          </div>
        ) : message ? (
          <Card className="bg-zinc-950 border-zinc-800">
            <CardContent className="py-12 text-center">
              <Buildings className="w-12 h-12 mx-auto mb-4 text-zinc-600" />
              <p className="text-zinc-400">{message}</p>
              <Button
                onClick={() => navigate("/profile")}
                className="mt-4 bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
              >
                Complete Profile
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Results Summary */}
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-zinc-500">
                {visibleScouts.length} {onlyMatchers ? "matching" : ""} compan{visibleScouts.length === 1 ? "y" : "ies"} &middot; {analyzedCount} analyzed &middot; {strongCount} strong matches
              </p>
              {pendingCount > 0 && (
                <span className="text-xs text-amber-400 flex items-center gap-1">
                  <ArrowsClockwise className="w-3 h-3 animate-spin" />
                  {pendingCount} analyzing in background...
                </span>
              )}
              {matcherCount > 0 && (
                <button
                  type="button"
                  onClick={() => setOnlyMatchers(v => !v)}
                  className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-sm border transition-colors ${
                    onlyMatchers
                      ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                      : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-emerald-500/30 hover:text-emerald-400"
                  }`}
                  data-testid="filter-matchers-toggle"
                  aria-pressed={onlyMatchers}
                >
                  <HandCoins weight="fill" className="w-3.5 h-3.5" />
                  {onlyMatchers ? "Showing employee-match programs only" : `Filter: Matches employee gifts (${matcherCount})`}
                </button>
              )}
            </div>

            {/* Scout Cards */}
            <div className="space-y-4">
              {visibleScouts.map((scout) => {
                const isPending = scout.pending || scout.match_score < 0;
                return (
                <Card
                  key={scout.company_id}
                  className={`bg-zinc-950 border-zinc-800 hover:border-zinc-700 transition-colors ${
                    scout.match_score >= 70 ? 'border-l-2 border-l-emerald-500' : ''
                  } ${isPending ? 'opacity-75' : ''}`}
                  data-testid={`scout-card-${scout.company_id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      {/* Company Info */}
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-12 h-12 rounded-sm bg-zinc-900 flex items-center justify-center flex-shrink-0">
                          <Buildings weight="duotone" className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="font-bold text-lg">{scout.company_name}</h3>
                            <ScoreBadge score={scout.match_score} pending={isPending} />
                            {scout.win_probability && !isPending && (
                              <WinProbabilityBadge winProbability={scout.win_probability} size="sm" />
                            )}
                            {scout.verified && (
                              <span className="text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-sm px-1.5 py-0.5 font-semibold uppercase tracking-wider" data-testid={`verified-badge-${scout.company_id}`}>Verified</span>
                            )}
                            {scout.matches_employee_gifts && (
                              <span
                                title={scout.match_notes || "This company matches employee donations to 501(c)(3) organizations."}
                                className="inline-flex items-center gap-1 text-[10px] bg-lime-500/10 text-lime-300 border border-lime-500/30 rounded-sm px-1.5 py-0.5 font-semibold uppercase tracking-wider"
                                data-testid={`match-gift-badge-${scout.company_id}`}
                              >
                                <HandCoins weight="fill" className="w-3 h-3" />
                                Matches Gifts {scout.match_ratio ? `· ${scout.match_ratio}` : ""}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-zinc-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />{scout.sc_location}
                            </span>
                            <span className="flex items-center gap-1">
                              <UsersThree className="w-3.5 h-3.5" />{scout.employees_sc} employees
                            </span>
                            <span className="text-zinc-600">{scout.industry}</span>
                          </div>

                          {/* Match Reasoning */}
                          {!isPending && <p className="text-sm text-zinc-300 mt-3">{scout.match_reasoning}</p>}

                          {/* Giving Pillars */}
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {scout.giving_pillars?.map((pillar, i) => (
                              <span key={i} className="text-xs px-2 py-0.5 bg-zinc-900 border border-zinc-800 rounded-sm text-zinc-400">
                                {pillar}
                              </span>
                            ))}
                          </div>

                          {/* Employee Matching Detail */}
                          {scout.matches_employee_gifts && (
                            <div className="mt-3 p-3 bg-lime-500/5 border border-lime-500/20 rounded-sm" data-testid={`match-detail-${scout.company_id}`}>
                              <p className="text-xs text-lime-300 font-mono uppercase tracking-wider mb-1 flex items-center gap-1">
                                <HandCoins weight="fill" className="w-3.5 h-3.5" />
                                Employee Gift Match · {scout.match_ratio || "1:1"} {scout.match_max ? `up to ${scout.match_max}` : ""}
                              </p>
                              <p className="text-sm text-zinc-300">{scout.match_notes}</p>
                              <p className="text-xs text-zinc-500 mt-1">
                                Tip: Ask their SC employees to donate to your nonprofit, then submit the company's match form. Your dollars effectively double.
                              </p>
                              {scout.match_program_url && (
                                <a
                                  href={scout.match_program_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-lime-400 hover:text-lime-300 mt-1 inline-block"
                                >
                                  Visit matching program page &rarr;
                                </a>
                              )}
                              <MatchingGiftAskScript
                                scout={scout}
                                orgName={user?.nonprofit_profile?.org_name}
                              />
                            </div>
                          )}

                          {/* Trigger Event */}
                          {scout.trigger_event && !isPending && (
                            <div className="mt-3 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-sm">
                              <p className="text-xs text-emerald-400 font-mono uppercase tracking-wider mb-1">Trigger Event</p>
                              <p className="text-sm text-zinc-300">{scout.trigger_event}</p>
                            </div>
                          )}

                          {/* Verified Giving Programs */}
                          {scout.verified && scout.giving_programs && (
                            <div className="mt-3 p-3 bg-blue-500/5 border border-blue-500/20 rounded-sm">
                              <p className="text-xs text-blue-400 font-mono uppercase tracking-wider mb-1">Verified Giving Programs</p>
                              <p className="text-sm text-zinc-300">{scout.giving_programs}</p>
                              {scout.how_to_apply && (
                                <>
                                  <p className="text-xs text-blue-400 font-mono uppercase tracking-wider mt-2 mb-1">How to Apply</p>
                                  <p className="text-sm text-zinc-300">{scout.how_to_apply}</p>
                                </>
                              )}
                              {scout.giving_website && (
                                <a href={scout.giving_website} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-400 hover:text-emerald-300 mt-2 inline-block">
                                  Visit giving page &rarr;
                                </a>
                              )}
                            </div>
                          )}

                          {/* How to Reach Out */}
                          {scout.contact_method && (
                            <div className="mt-3 p-3 bg-zinc-900 border border-zinc-800 rounded-sm">
                              <p className="text-xs text-zinc-400 font-mono uppercase tracking-wider mb-1">How to Reach Out</p>
                              <p className="text-sm text-zinc-300">{scout.contact_method}</p>
                              {scout.contact_email && (
                                <p className="text-sm text-emerald-400 font-mono mt-1">{scout.contact_email}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions — 2-col grid on mobile (wraps cleanly within phone width),
                          vertical stack on desktop (lg+). Previous flex-row caused
                          the 4 buttons to overflow horizontally on phones. */}
                      <div className="grid grid-cols-2 lg:flex lg:flex-col gap-2 lg:flex-shrink-0 lg:w-44">
                        {!isPending && scout.warm_intro && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm w-full"
                                data-testid={`view-pitch-${scout.company_id}`}
                              >
                                <EnvelopeSimple className="w-4 h-4 mr-1" />
                                View Pitch
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                  <EnvelopeSimple className="w-5 h-5 text-emerald-400" />
                                  Pitch Letter — {scout.company_name}
                                </DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                {scout.contact_method && (
                                  <div className="p-3 bg-zinc-900 border border-zinc-800 rounded-sm">
                                    <p className="text-xs text-zinc-400 font-mono uppercase tracking-wider mb-1">How to Reach Out</p>
                                    <p className="text-sm text-zinc-300">{scout.contact_method}</p>
                                    {scout.contact_email && (
                                      <p className="text-sm text-emerald-400 font-mono mt-1">{scout.contact_email}</p>
                                    )}
                                  </div>
                                )}
                                {scout.pitch_strategy && (
                                  <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-sm">
                                    <p className="text-xs text-emerald-400 font-mono uppercase tracking-wider mb-1">Strategy</p>
                                    <p className="text-sm text-zinc-300">{scout.pitch_strategy}</p>
                                  </div>
                                )}
                                <div className="bg-zinc-900 p-5 rounded-sm">
                                  <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans leading-relaxed" data-testid="pitch-letter-content">
                                    {scout.warm_intro}
                                  </pre>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    onClick={() => openInGmail(scout)}
                                    className="flex-1 bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                                    data-testid="open-gmail-btn"
                                  >
                                    <EnvelopeSimple className="w-4 h-4 mr-2" />{scout.contact_email ? "Send Pitch" : "Draft Email"}
                                  </Button>
                                  <Button
                                    onClick={() => copyPitch(scout.warm_intro)}
                                    variant="outline"
                                    className="border-zinc-700 rounded-sm"
                                    data-testid="copy-pitch-btn"
                                  >
                                    {copied ? <><CheckCircle className="w-4 h-4 mr-1" />Copied</> : <><Copy className="w-4 h-4 mr-1" />Copy</>}
                                  </Button>
                                  {scout.contact_url && (
                                    <a href={scout.contact_url} target="_blank" rel="noopener noreferrer">
                                      <Button variant="outline" className="border-zinc-700 rounded-sm">
                                        <Globe className="w-4 h-4 mr-1" />CSR Page
                                      </Button>
                                    </a>
                                  )}
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        )}
                        {scout.contact_url && (
                          <a href={scout.contact_url} target="_blank" rel="noopener noreferrer" className="w-full">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-zinc-700 rounded-sm w-full"
                              data-testid={`csr-link-${scout.company_id}`}
                            >
                              <Globe className="w-4 h-4 mr-1" />
                              CSR Page
                            </Button>
                          </a>
                        )}
                        <a
                          href={linkedinSearchUrl(scout)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full"
                          title="Opens a LinkedIn People search pre-filled for this company's CSR / Community Relations team in South Carolina."
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-zinc-700 rounded-sm w-full hover:border-[#0A66C2] hover:text-[#0A66C2]"
                            data-testid={`linkedin-search-${scout.company_id}`}
                          >
                            <LinkedinLogo className="w-4 h-4 mr-1" />
                            Find Contact
                          </Button>
                        </a>
                        {!isPending && (
                          <Button
                            size="sm"
                            onClick={() => setProposalScout(scout)}
                            className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 rounded-sm w-full"
                            data-testid={`sponsorship-proposal-${scout.company_id}`}
                          >
                            <HandHeart weight="fill" className="w-4 h-4 mr-1" />
                            Sponsorship Proposal
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Sponsorship Proposal Modal (single instance — `proposalScout` controls which company) */}
      <SponsorshipProposalModal
        scout={proposalScout || {}}
        open={!!proposalScout}
        onOpenChange={(o) => { if (!o) setProposalScout(null); }}
      />
    </DashboardLayout>
  );
};

export default CorporateScout;
